import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const HiringTrendsTab = ({ dateRange }) => {
  const [trendView, setTrendView] = useState('monthly');
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  const trendViewOptions = [
    { value: 'monthly', label: 'Monthly View' },
    { value: 'quarterly', label: 'Quarterly View' },
    { value: 'yearly', label: 'Yearly View' }
  ];

  const industryOptions = [
    { value: 'all', label: 'All Industries' },
    { value: 'it', label: 'Information Technology' },
    { value: 'finance', label: 'Banking & Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'manufacturing', label: 'Manufacturing' }
  ];

  const monthlyTrendData = [
    { month: 'Aug 2025', placements: 145, applications: 320, interviews: 245 },
    { month: 'Sep 2025', placements: 168, applications: 385, interviews: 290 },
    { month: 'Oct 2025', placements: 192, applications: 425, interviews: 335 },
    { month: 'Nov 2025', placements: 215, applications: 480, interviews: 380 },
    { month: 'Dec 2025', placements: 188, applications: 410, interviews: 325 },
    { month: 'Jan 2026', placements: 225, applications: 520, interviews: 410 }
  ];

  const industryDemandData = [
    { industry: 'IT', demand: 342, growth: 18.5 },
    { industry: 'Finance', demand: 245, growth: 12.3 },
    { industry: 'Healthcare', demand: 198, growth: 15.7 },
    { industry: 'Manufacturing', demand: 165, growth: 8.2 },
    { industry: 'E-commerce', demand: 142, growth: 22.4 },
    { industry: 'Consulting', demand: 128, growth: 10.1 }
  ];

  const salaryTrendData = [
    { range: '< ₹4L', count: 125, avgSalary: 3.2 },
    { range: '₹4-6L', count: 342, avgSalary: 5.1 },
    { range: '₹6-8L', count: 428, avgSalary: 7.2 },
    { range: '₹8-12L', count: 245, avgSalary: 9.8 },
    { range: '> ₹12L', count: 108, avgSalary: 16.5 }
  ];

  const seasonalPatterns = [
    { season: 'Q1 (Jan-Mar)', placements: 645, trend: 'High' },
    { season: 'Q2 (Apr-Jun)', placements: 420, trend: 'Medium' },
    { season: 'Q3 (Jul-Sep)', placements: 385, trend: 'Low' },
    { season: 'Q4 (Oct-Dec)', placements: 598, trend: 'High' }
  ];

  const handleExportTrends = () => {
    const csvContent = [
      ['Month', 'Placements', 'Applications', 'Interviews'],
      ...monthlyTrendData?.map(row => [row?.month, row?.placements, row?.applications, row?.interviews])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hiring-trends-${Date.now()}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          options={trendViewOptions}
          value={trendView}
          onChange={setTrendView}
          className="w-full md:w-48"
        />
        <Select
          options={industryOptions}
          value={selectedIndustry}
          onChange={setSelectedIndustry}
          className="w-full md:w-48"
        />
        <Button variant="outline" iconName="Download" onClick={handleExportTrends}>
          Export Trends
        </Button>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Placement Trends Over Time</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={monthlyTrendData}>
            <defs>
              <linearGradient id="colorPlacements" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
            <YAxis stroke="var(--color-muted-foreground)" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'var(--color-card)', 
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Area type="monotone" dataKey="placements" stroke="var(--color-primary)" fillOpacity={1} fill="url(#colorPlacements)" />
            <Area type="monotone" dataKey="applications" stroke="var(--color-accent)" fillOpacity={1} fill="url(#colorApplications)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Industry-wise Demand</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={industryDemandData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="industry" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="demand" fill="var(--color-success)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Salary Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={salaryTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="range" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="count" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Seasonal Hiring Patterns</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {seasonalPatterns?.map((pattern, idx) => (
            <div key={idx} className="p-4 rounded-lg border border-border hover:border-primary transition-smooth">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-card-foreground">{pattern?.season}</h4>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  pattern?.trend === 'High' ? 'bg-success/10 text-success' :
                  pattern?.trend === 'Medium'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                }`}>
                  {pattern?.trend}
                </span>
              </div>
              <p className="text-2xl font-bold text-card-foreground">{pattern?.placements}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Placements</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Industry Growth Rates</h3>
        <div className="space-y-3">
          {industryDemandData?.map((industry, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-card-foreground">{industry?.industry}</span>
                  <span className="text-sm font-semibold text-success">+{industry?.growth}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-success transition-smooth"
                    style={{ width: `${industry?.growth * 3}%` }}
                  />
                </div>
              </div>
              <span className="text-sm text-muted-foreground w-16 text-right">{industry?.demand} jobs</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HiringTrendsTab;